"use client";

import { useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import type { FacetSet } from "./MegaMenu";

/**
 * Mobile menu — hamburger button + slide-in drawer with collapsible sections.
 *
 * Accordion sections are <details>/<summary> so they're keyboard-accessible
 * by default and don't need extra ARIA wiring.
 *
 * Drawer uses native <dialog> for focus trap + Esc close.
 */
export function MobileMenu({ facets }: { facets: FacetSet }) {
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <>
      <button
        type="button"
        aria-label="Open menu"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className="inline-flex h-11 w-11 items-center justify-center rounded-sm text-[var(--color-bone)] hover:text-[var(--color-foil)] md:hidden"
      >
        <BurgerIcon className="h-5 w-5" />
      </button>

      <dialog
        ref={dialogRef}
        onClose={() => setOpen(false)}
        aria-labelledby={titleId}
        className="m-0 h-dvh max-h-none w-[88vw] max-w-sm bg-[var(--color-ink)] p-0 text-[var(--color-bone)] backdrop:bg-black/70 backdrop:backdrop-blur-sm"
        style={{ marginLeft: 0, marginRight: "auto" }}
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[var(--color-bone)]/15 px-5 py-4">
            <h2 id={titleId} className="font-display text-base uppercase tracking-tight">
              Shop
            </h2>
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setOpen(false)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-sm text-[var(--color-bone-dim)] hover:text-[var(--color-bone)]"
            >
              <CloseIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            <FacetAccordion
              title="By Brand"
              facets={facets.brand}
              paramKey="brand"
              onLink={() => setOpen(false)}
              defaultOpen
            />
            <FacetAccordion
              title="By Model"
              facets={facets.model}
              paramKey="model"
              onLink={() => setOpen(false)}
            />
            <FacetAccordion
              title="By Collab"
              facets={facets.collab}
              paramKey="collab"
              onLink={() => setOpen(false)}
            />
            <details className="border-b border-[var(--color-bone)]/10 py-3">
              <summary className="mono-strip flex cursor-pointer list-none items-center justify-between py-2 text-[var(--color-bone)] hover:text-[var(--color-foil)] [&::-webkit-details-marker]:hidden">
                <span>By Size</span>
                <Chevron className="h-2.5 w-2.5 transition-transform group-open:rotate-180" />
              </summary>
              <div className="mt-3 grid grid-cols-4 gap-1.5">
                {facets.size.map((f) => (
                  <Link
                    key={f.value}
                    href={`/shop?size=${encodeURIComponent(f.value)}`}
                    onClick={() => setOpen(false)}
                    className="rounded-sm border border-[var(--color-bone)]/15 px-2 py-2 text-center font-mono text-xs text-[var(--color-bone)] hover:border-[var(--color-blood)] hover:text-[var(--color-blood)]"
                  >
                    {f.value}
                  </Link>
                ))}
              </div>
            </details>

            <div className="mt-6 border-t border-[var(--color-bone)]/15 pt-6">
              <div className="mono-strip mb-3 text-[var(--color-bone-dim)]">Main</div>
              <ul className="space-y-3">
                <li>
                  <Link
                    href="/shop"
                    onClick={() => setOpen(false)}
                    className="font-display text-lg uppercase hover:text-[var(--color-blood)]"
                  >
                    Full catalog
                  </Link>
                </li>
                <li>
                  <Link
                    href="/contact"
                    onClick={() => setOpen(false)}
                    className="font-display text-lg uppercase hover:text-[var(--color-blood)]"
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </dialog>
    </>
  );
}

function FacetAccordion({
  title,
  facets,
  paramKey,
  onLink,
  defaultOpen,
}: {
  title: string;
  facets: { value: string; label: string; count: number }[];
  paramKey: string;
  onLink: () => void;
  defaultOpen?: boolean;
}) {
  if (facets.length === 0) return null;
  return (
    <details className="group border-b border-[var(--color-bone)]/10 py-3" open={defaultOpen}>
      <summary className="mono-strip flex cursor-pointer list-none items-center justify-between py-2 text-[var(--color-bone)] hover:text-[var(--color-foil)] [&::-webkit-details-marker]:hidden">
        <span>{title}</span>
        <Chevron className="h-2.5 w-2.5 transition-transform group-open:rotate-180" />
      </summary>
      <ul className="mt-2 space-y-1">
        {facets.map((f) => (
          <li key={f.value}>
            <Link
              href={`/shop?${paramKey}=${encodeURIComponent(f.value)}`}
              onClick={onLink}
              className="flex items-baseline justify-between rounded-sm px-3 py-2 font-display text-sm uppercase text-[var(--color-bone)] hover:bg-[var(--color-bone)]/5 hover:text-[var(--color-blood)]"
            >
              <span>{f.label}</span>
              <span className="mono-strip text-[var(--color-bone-dim)]">{f.count}</span>
            </Link>
          </li>
        ))}
      </ul>
    </details>
  );
}

function BurgerIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M4 7h16M4 12h16M4 17h16"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function Chevron({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 12 8" fill="none" className={className} aria-hidden="true">
      <path d="M1 1l5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
